import { botLink } from '../domain/deepLink';
import { shareLink } from '../domain/invite';

/**
 * The only place that touches window.Telegram.WebApp. Features use this typed surface, so tests, the browser
 * (telegram/browser.ts) and Max (telegram/max.ts) substitute one object. Spec §8.1.
 */

interface RawWebApp {
  initData?: string;
  colorScheme?: 'light' | 'dark';
  platform?: string;
  version?: string;
  ready?: () => void;
  expand?: () => void;
  requestContact?: (callback: (shared: boolean) => void) => void;
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  HapticFeedback?: { notificationOccurred?: (type: 'success' | 'error' | 'warning') => void };
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  /** Bot API 8.0+: Telegram's own "add to home screen" dialog and its status. */
  addToHomeScreen?: () => void;
  checkHomeScreenStatus?: (cb: (status: string) => void) => void;
  CloudStorage?: {
    getItem: (key: string, cb: (err: string | null, value?: string) => void) => void;
    setItem: (key: string, value: string, cb?: (err: string | null, ok?: boolean) => void) => void;
  };
}

/** CloudStorage calls that take longer read as "no value". */
export const STORAGE_TIMEOUT_MS = 3_000;

export type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed';

export type Messenger = 'telegram' | 'max';

/** Max hands the phone to the mini app itself, signed with the bot token; mobile-api checks the hash. */
export interface SignedContact {
  phone: string;
  authDate: string;
  hash: string;
}

export interface TelegramApi {
  /** The messenger the app runs in; null in an ordinary browser (telegram/browser.ts): no initData, no native buttons, sign-in by hand-off. */
  messenger: Messenger | null;
  /** For copy: «Войти по номеру из Telegram». The browser signs in through Telegram, so it says Telegram too. */
  name: 'Telegram' | 'Max';
  /** Raw signed string; sent to the server as is, never parsed (login contract). */
  initData: string;
  colorScheme: 'light' | 'dark';
  /** Telegram client platform and Bot API version, for error reports only. */
  platform: string;
  version: string;
  ready(): void;
  /**
   * Shows the messenger's "share phone number" dialog; false when the resident declined. Telegram sends the phone to
   * the bot and answers true; Max answers with the signed contact for mobile-api.
   */
  requestContact(): Promise<boolean | SignedContact>;
  openLink(url: string): void;
  /** The messenger's own share dialog for a link with a message. */
  share(link: string, text: string): void;
  /** The link that opens this mini app in its messenger, with an optional start parameter (domain/deepLink.ts). */
  appLink(startParam?: string): string;
  notify(type: 'success' | 'error'): void;
  /** Shows Telegram's native back button for nested screens; the returned function hides it again. */
  showBackButton(onBack: () => void): () => void;
  /** Home-screen shortcut: 'unsupported' on old clients and desktop, 'added' once the resident has it. */
  homeScreen: {
    status(): Promise<HomeScreenStatus>;
    add(): void;
  };
  /** Telegram CloudStorage: survives reinstall and device change. Failures read as null. */
  storage: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
  };
}

export function getTelegram(win: Window = window): TelegramApi | null {
  const webApp = (win as unknown as { Telegram?: { WebApp?: RawWebApp } }).Telegram?.WebApp;
  if (!webApp?.initData) return null;

  const openTelegramLink = (url: string) => {
    if (webApp.openTelegramLink) webApp.openTelegramLink(url);
    else win.open(url, '_blank', 'noopener');
  };

  return {
    messenger: 'telegram',
    name: 'Telegram',
    initData: webApp.initData,
    colorScheme: webApp.colorScheme === 'dark' ? 'dark' : 'light',
    platform: webApp.platform ?? 'unknown',
    version: webApp.version ?? '',
    ready() {
      webApp.ready?.();
      webApp.expand?.();
    },
    requestContact() {
      return new Promise((resolve) => {
        if (!webApp.requestContact) return resolve(false);
        webApp.requestContact((shared) => resolve(Boolean(shared)));
      });
    },
    openLink(url) {
      if (webApp.openLink) webApp.openLink(url);
      else win.open(url, '_blank', 'noopener');
    },
    // t.me links open inside Telegram instead of the browser.
    share(link, text) {
      openTelegramLink(shareLink(link, text));
    },
    appLink: (startParam) => appLink(botLink(), startParam),
    notify(type) {
      webApp.HapticFeedback?.notificationOccurred?.(type);
    },
    showBackButton(onBack) {
      const button = webApp.BackButton;
      if (!button) return () => {};
      button.onClick(onBack);
      button.show();
      return () => {
        button.offClick(onBack);
        button.hide();
      };
    },
    homeScreen: {
      status() {
        return new Promise((resolve) => {
          if (!webApp.checkHomeScreenStatus || !webApp.addToHomeScreen) return resolve('unsupported');
          webApp.checkHomeScreenStatus((status) =>
            resolve(status === 'added' || status === 'missed' || status === 'unknown' ? status : 'unsupported'),
          );
        });
      },
      add() {
        webApp.addToHomeScreen?.();
      },
    },
    storage: {
      get(key) {
        return new Promise((resolve) => {
          if (!webApp.CloudStorage) return resolve(null);
          // Some clients never call back; a missing value is better than a splash that never ends.
          const timer = setTimeout(() => resolve(null), STORAGE_TIMEOUT_MS);
          webApp.CloudStorage.getItem(key, (err, value) => {
            clearTimeout(timer);
            resolve(err || !value ? null : value);
          });
        });
      },
      set(key, value) {
        return new Promise((resolve) => {
          if (!webApp.CloudStorage) return resolve();
          const timer = setTimeout(resolve, STORAGE_TIMEOUT_MS);
          webApp.CloudStorage.setItem(key, value, () => {
            clearTimeout(timer);
            resolve();
          });
        });
      },
    },
  };
}

export function appLink(bot: string, startParam?: string): string {
  return startParam ? `${bot}?startapp=${startParam}` : bot;
}
